import React, { useState } from "react";
import {
  Menu,
  Description,
  Public,
  Lightbulb,
  Event,
  Assessment,
} from "@mui/icons-material";
import { useNavigate, useLocation, useMatches } from "react-router-dom";
import { useServices } from "../../services/Services";
import { InsightType, RouteHandle } from "../../type/enum";
import css from "./Sidebar.module.css";
import sidebarApplicationLogo from "/unruly-logo-color-nobg-rect.png";
import SidebarPDBSection from "./SidebarPDBSection";
import SidebarTopicSection from "./SidebarTopicSection";
import SidebarEventSection from "./SidebarEventSection";
import SidebarCustomReportSection from "./SidebarCustomReportSection";
import SidebarUserProfile from "./SidebarUserProfile";

interface SidebarProps {
  onCollapseChange?: (isCollapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onCollapseChange }) => {
  const navigate = useNavigate();
  const services = useServices();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  React.useEffect(() => {
    function handleMobileSidebarToggle() {
      setIsCollapsed((prev) => {
        const newCollapsed = !prev;
        onCollapseChange?.(newCollapsed);
        return newCollapsed;
      });
      setHoveredItem(null);
    }

    window.addEventListener(
      "mobileSidebarToggle",
      handleMobileSidebarToggle as EventListener,
    );
    return () =>
      window.removeEventListener(
        "mobileSidebarToggle",
        handleMobileSidebarToggle as EventListener,
      );
  }, [onCollapseChange]);

  const { data: user } = services.User.useGetCurrent();
  const { hasAccess: hasCustomReportsAccess } =
    services.User.useIsInCustomReportsGroup();

  const matches = useMatches();
  const getActiveSection = (
    matches: ReturnType<typeof useMatches>,
  ): InsightType | null => {
    const currentMatch = matches[matches.length - 1];
    return (currentMatch?.handle as RouteHandle)?.sidebarSection || null;
  };

  const activeSection = getActiveSection(matches);

  const [selectedSection, setSelectedSection] = useState<InsightType | null>(
    null,
  );

  const displayedSection =
    selectedSection || activeSection || InsightType.PersonalDailyBriefs;

  const isCurrentRoute = (path: string) => {
    return location.pathname === path;
  };

  function toggleCollapse() {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    onCollapseChange?.(newCollapsed);
    setHoveredItem(null);
  }

  function handleLogoClick() {
    navigate("/");
  }

  function handleMouseEnter(itemId: string, event: React.MouseEvent) {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 10,
    });
    setHoveredItem(itemId);
  }

  function onItemSelect() {
    setIsCollapsed(true);
  }

  return (
    <div className={`${css.sidebar} ${isCollapsed ? css.collapsed : ""}`}>
      <div className={css.header}>
        <div
          className={css.sidebarToggle}
          onClick={toggleCollapse}
          onMouseEnter={(e) => handleMouseEnter("sidebarToggle", e)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <div className={css.iconContainer}>
            <Menu className={css.icon} />
          </div>
          <span className={css.label}>Toggle Sidebar</span>
          {isCollapsed && hoveredItem === "sidebarToggle" && (
            <div
              className={css.tooltip}
              style={{
                position: "fixed",
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
                transform: "translateY(-50%)",
              }}
            >
              Toggle Sidebar
            </div>
          )}
        </div>
        <div className={css.logoContainer}>
          <img
            src={sidebarApplicationLogo}
            alt="Unruly"
            className={css.logo}
            onClick={handleLogoClick}
            style={{ cursor: "pointer" }}
          />
        </div>
      </div>

      <div className={css.mainContent}>
        <div className={css.primaryNav}>
          <div
            onClick={() => {
              setSelectedSection(InsightType.PersonalDailyBriefs);
              if (isCollapsed) {
                setIsCollapsed(false);
                onCollapseChange?.(false);
              }
            }}
            className={`${activeSection === InsightType.PersonalDailyBriefs ? css.navIconActive : css.navIcon}
              ${selectedSection === InsightType.PersonalDailyBriefs ? css.navIconSelected : ""}`}
          >
            <Description className={css.navIconSvg} />
            <span className={css.navLabel} title="Personal Daily Briefs">
              Briefs
            </span>
          </div>
          <div
            onClick={() => {
              onItemSelect();
              navigate("/global-research");
            }}
            className={`${activeSection === InsightType.GlobalReports ? css.navIconActive : css.navIcon}
              ${selectedSection === InsightType.GlobalReports ? css.navIconSelected : ""}`}
          >
            <Public className={css.navIconSvg} />
            <span className={css.navLabel} title="Global Research">
              Global
            </span>
          </div>
          <div
            onClick={() => {
              setSelectedSection(InsightType.TopicDeepDives);
              if (isCollapsed) {
                setIsCollapsed(false);
                onCollapseChange?.(false);
              }
            }}
            className={`${activeSection === InsightType.TopicDeepDives ? css.navIconActive : css.navIcon}
              ${selectedSection === InsightType.TopicDeepDives ? css.navIconSelected : ""}`}
          >
            <Lightbulb className={css.navIconSvg} />
            <span className={css.navLabel} title="Topic Deep Dives">
              Topics
            </span>
          </div>
          <div
            onClick={() => {
              setSelectedSection(InsightType.EventDeepDives);
              if (isCollapsed) {
                setIsCollapsed(false);
                onCollapseChange?.(false);
              }
            }}
            className={`${activeSection === InsightType.EventDeepDives ? css.navIconActive : css.navIcon}
              ${selectedSection === InsightType.EventDeepDives ? css.navIconSelected : ""}`}
          >
            <Event className={css.navIconSvg} />
            <span className={css.navLabel} title="Event Deep Dives">
              Events
            </span>
          </div>
          {hasCustomReportsAccess && (
            <div
              onClick={() => {
                setSelectedSection(InsightType.CustomReports);
                if (isCollapsed) {
                  setIsCollapsed(false);
                  onCollapseChange?.(false);
                }
              }}
              className={`${activeSection === InsightType.CustomReports ? css.navIconActive : css.navIcon}
                ${selectedSection === InsightType.CustomReports ? css.navIconSelected : ""}`}
            >
              <div className={css.navIconWrapper}>
                <Assessment className={css.navIconSvg} />
                <span className={css.betaBadge}>BETA</span>
              </div>
              <span className={css.navLabel} title="Custom Reports">
                Custom
              </span>
            </div>
          )}
        </div>
        {!isCollapsed && (
          <div className={css.secondaryContent}>
            <div className={css.contentList}>
              {displayedSection === InsightType.PersonalDailyBriefs &&
                user?.id && (
                  <SidebarPDBSection
                    userId={user.id}
                    isCurrentRoute={isCurrentRoute}
                    onItemSelect={onItemSelect}
                  />
                )}

              {displayedSection === InsightType.TopicDeepDives && user?.id && (
                <SidebarTopicSection
                  userId={user.id}
                  isCurrentRoute={isCurrentRoute}
                  onItemSelect={onItemSelect}
                />
              )}

              {displayedSection === InsightType.EventDeepDives && user?.id && (
                <SidebarEventSection
                  userId={user.id}
                  isCurrentRoute={isCurrentRoute}
                  onItemSelect={onItemSelect}
                />
              )}

              {displayedSection === InsightType.CustomReports &&
                user?.id &&
                hasCustomReportsAccess && (
                  <SidebarCustomReportSection
                    userId={user.id}
                    isCurrentRoute={isCurrentRoute}
                    onItemSelect={onItemSelect}
                  />
                )}
            </div>
          </div>
        )}
      </div>

      <SidebarUserProfile />
    </div>
  );
};

export default Sidebar;
